import axios from 'axios';
import BaseEdit from 'components/Base/BaseEdit';
import React, { useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import { currentUSStateCodes } from 'utils/constants';
import {
    UserIcon,
    MapPinIcon,
    EnvelopeIcon,
    PhoneIcon,
    CheckCircleIcon,
    InformationCircleIcon,
    ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from 'react-router-dom';

const PatientCreate = () => {
    const [name, setName] = useState('');
    const [surname, setSurname] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [about, setAbout] = useState('');
    const [location, setLocation] = useState({
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zipCode: '',
    });
    const [isAddressVerified, setIsAddressVerified] = useState(false);
    const [error, setError] = useState({});
    const navigate = useNavigate();

    const verifyAddress = () => {
        axios
            .post('/auth/signup/address/confirm', { location })
            .then(({ data }) => {
                setIsAddressVerified(data.success);
                setError({});
            })
            .catch((error) => {
                setIsAddressVerified(false);
                setError({
                    type: error.response?.data?.error || 'verification',
                    message: error.response?.data?.message || 'Failed to verify address',
                });
            });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!name || !surname || !email || !phone || !about ||
            !location.addressLine1 || !location.city || !location.state || !location.zipCode) {
            setError({
                type: 'validation',
                message: 'Please fill in all required fields.',
            });
            return;
        }

        if (!isAddressVerified) {
            setError({
                type: 'validation',
                message: 'Please verify the address before submitting.',
            });
            return;
        }

        const patientData = { name, surname, email, phone, about, location };

        axios.post('/patients', patientData)
            .then(({ data }) => {
                console.log('Patient created successfully:', data);
                setError({});
                navigate('/')
            })
            .catch(error => {
                console.error('Error creating patient:', error);
                setError({
                    type: 'server',
                    message: error.response?.data.error || 'Failed to create patient',
                });
            });
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-sky-50">
            <div className="mx-auto pt-8" style={{ maxWidth: '1100px' }}>
                {/* Header Section */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-blue-500 via-sky-500 to-sky-700 bg-clip-text text-transparent">
                        Create New Patient
                    </h1>
                    <p className="mt-2 text-sm text-gray-600">
                        Add a new patient to your care management system
                    </p>
                </div>

                {/* Main Form */}
                <form onSubmit={handleSubmit} className="space-y-8">
                    {/* Personal Information Card */}
                    <div className="bg-white rounded-2xl p-6 shadow-lg shadow-sky-900/5 ring-1 ring-slate-100">
                        <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                            <UserIcon className="h-5 w-5 text-sky-500 mr-2" />
                            Personal Information
                        </h2>

                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    First Name
                                </label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                                    placeholder="Enter first name"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Last Name
                                </label>
                                <input
                                    type="text"
                                    value={surname}
                                    onChange={(e) => setSurname(e.target.value)}
                                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                                    placeholder="Enter last name"
                                />
                            </div>
                        </div>

                        <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    <div className="flex items-center">
                                        <EnvelopeIcon className="h-4 w-4 text-gray-400 mr-1" />
                                        Email Address
                                    </div>
                                </label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                                    placeholder="patient@example.com"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    <div className="flex items-center">
                                        <PhoneIcon className="h-4 w-4 text-gray-400 mr-1" />
                                        Number
                                    </div>
                                </label>
                                <PhoneInput
                                    country={'us'}
                                    value={phone}
                                    specialLabel=''
                                    onChange={(phone) => setPhone(phone)}
                                    containerClass="phone-input-container"
                                    inputClass="!w-full !h-[38px] !rounded-lg !py-2 !px-3 !text-sm !border-gray-300"
                                    buttonClass="!rounded-l-lg !h-[38px]"
                                />
                            </div>
                        </div>
                    </div>

                    {/* About Section */}
                    <div className="bg-white rounded-2xl p-6 shadow-lg shadow-sky-900/5 ring-1 ring-slate-100">
                        <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                            <InformationCircleIcon className="h-5 w-5 text-sky-500 mr-2" />
                            About Patient
                        </h2>

                        <BaseEdit
                            name={'about'}
                            value={about}
                            onChange={(about) => setAbout(about)}
                            className="min-h-[120px]"
                        />
                    </div>

                    {/* Address Section */}
                    <div className="bg-white rounded-2xl p-6 shadow-lg shadow-sky-900/5 ring-1 ring-slate-100">
                        <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                            <MapPinIcon className="h-5 w-5 text-sky-500 mr-2" />
                            Address Information
                        </h2>

                        <div className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Address Line 1
                                </label>
                                <input
                                    type="text"
                                    value={location.addressLine1}
                                    onChange={(e) => setLocation(prev => ({ ...prev, addressLine1: e.target.value }))}
                                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                                    placeholder="Street address"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Address Line 2
                                </label>
                                <input
                                    type="text"
                                    value={location.addressLine2}
                                    onChange={(e) => setLocation(prev => ({ ...prev, addressLine2: e.target.value }))}
                                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                                    placeholder="Apartment, suite, etc. (optional)"
                                />
                            </div>

                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        City
                                    </label>
                                    <input
                                        type="text"
                                        value={location.city}
                                        onChange={(e) => setLocation(prev => ({ ...prev, city: e.target.value }))}
                                        className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        State
                                    </label>
                                    <select
                                        value={location.state}
                                        onChange={(e) => setLocation(prev => ({ ...prev, state: e.target.value }))}
                                        className="block w-full h-[38px] rounded-lg border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm pl-2"
                                    >
                                        <option value="" className="py-2">Select a state</option>
                                        {Object.entries(currentUSStateCodes).map(([code, name]) => (
                                            <option key={code} value={code} className="py-2">{name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        ZIP Code
                                    </label>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={location.zipCode}
                                            onChange={(e) => setLocation(prev => ({ ...prev, zipCode: e.target.value }))}
                                            className="block w-full h-[38px] rounded-lg border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                                        />
                                        <button
                                            type="button"
                                            onClick={verifyAddress}
                                            disabled={!['addressLine1', 'city', 'state', 'zipCode'].every(key => location[key]) || isAddressVerified}
                                            className={`flex items-center h-[38px] px-4 rounded-lg text-sm font-medium transition-all duration-200 min-w-[120px] justify-center ${isAddressVerified
                                                ? 'bg-green-50 text-green-700 ring-1 ring-green-600/20'
                                                : 'bg-sky-50 text-sky-700 ring-1 ring-sky-600/20 hover:bg-sky-100'
                                                }`}
                                        >
                                            {isAddressVerified ? (
                                                <>
                                                    <CheckCircleIcon className="h-4 w-4 mr-1" />
                                                    Verified
                                                </>
                                            ) : (
                                                'Verify Address'
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Error Message */}
                    {error.message && (
                        <div className="rounded-lg bg-red-50 p-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <InformationCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-red-800">
                                        {error.type}
                                    </h3>
                                    <p className="text-sm text-red-700 mt-1">
                                        {error.message}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Submit Button */}
                    <div className="flex justify-end">
                        <button
                            type="submit"
                            disabled={!(isAddressVerified && email && phone && name && surname)}
                            className="inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                        >
                            <span className="flex items-center gap-2">
                                Create Patient
                                <ChevronRightIcon className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                            </span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PatientCreate;